import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  chakra,
  Image,
  Text,
  Input,
  useBreakpointValue,
  Grid,
  Modal,
  ModalContent,
} from '@chakra-ui/react';
import { render } from 'datocms-structured-text-to-html-string';
import { mixPanel } from '@utils/Mixpanel';
import debounce from 'lodash/debounce';
import { keyframes } from '@emotion/react';

/* Internal Imports */
/* Components */
import StructuredTextParser from '@components/Utility/ArticleParser/StructuredTextParser';
import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { useRegisterPopupVisibilityContext } from '@landing_pages/margazhi/context/registerPopupVisibilityContext';

export default function FaqV3({ faqSection, region, lang }) {
  const router = useRouter();
  const Mixpanel = mixPanel(router?.asPath);
  const { isFaqOpen, onFaqOpen, onFaqClose, isFaqMounted, isLargeScreen } = useRegisterPopupVisibilityContext();
  let [searchText, setSearchText] = useState('');
  let [filteredFaqs, setFilteredFaqs] = useState([]);
  let [relatedFaqs, setRelatedFaqs] = useState([]);
  let [showRelatedFaqs, toggleShowRelatedFaqs] = useState(false);
  

  const mixPanelTrigger = (value, isExpanded) => {
    if (!isExpanded) {
      console.log('value, isExpanded', value, isExpanded);

      Mixpanel.track('margazhi_faq_drop_down', {
        faq_keyword: value,
      });
    }
  };

  const debouncedFn = useMemo(
    () =>
      debounce((value, matchedQues = []) => {
        Mixpanel.track('margazhi_faq_search_query', {
          search_query: value,
          result_count: matchedQues?.length || 0,
        });
      }, 1000),
    []
  );

  const renderFaqSection = section => {
  return section.map((faqObj, i) => {
    if (faqObj._modelApiKey != 'faq_section_v2') {
      return null;
    }

    const getChildren = faqItems => {
      return faqItems?.map((faq, j) => {
        return (
          <AccordionItem
            key={`faq-${i}-${j}`}
            borderBottom={j === faqItems.length - 1 ? 'none !important' : "0.5px solid rgba(29, 26, 22, 0.20)"}
            borderTop="none"
            lineHeight="1.61"
            mx={{ base: '20px', md: '0px' }}
            _hover={{
                  base: {
                    background: '#fff',
                  },
                  lg: {
                    background: '#1D1A1608',
                  }

                }}
          >
            {({ isExpanded }) => (
              <>
                <AccordionButton
                  pt={4}
                  pb={4}
                  px={{ base: '0px', md: "24px" }}
                  mb={isExpanded ? '-1.8rem' : 'unset'}
                  fontFamily="FedraSansStd-book"
                  borderTop="none"
                  _focus={{ shadow: 'transparent' }}
                  _hover={{
                    background: 'none !important',

                }}
                  alignItems={'flex-start'}
                  onClick={() => mixPanelTrigger(faq.keyword, isExpanded)}
                >
                  <chakra.div
                    flex="1"
                    textAlign="left"
                    color={'#1D1A16'}
                    fontSize={{ base: '14px', md: '16px' }}
                    fontFamily="FedraSansStd-medium"
                    letterSpacing={{ base: '0.28px', md: '0.32px' }}
                    lineHeight={{base: '21px', md: '24px'}}
                  >
                    {faq.question}
                  </chakra.div>

                  <svg
                    style={{ transform: isExpanded ? '' : 'rotate(180deg)' }}
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8 14L12 10L16 14"
                      stroke="#CF4520"
                      strokeWidth="2"
                      strokeLinecap="square"
                    />
                  </svg>
                </AccordionButton>
                <AccordionPanel
                  pb={3}
                  fontFamily="FedraSansStd-book"
                  lineHeight="1.61"
                  px={{ base: '0px', md: "24px" }}
                >
                  <Box display="block" width="100%">
                    <Box className="faqAns">
                      <StructuredTextParser
                        str={render(faq.answer)}
                        region={region}
                        lang={lang}
                        fontSize={{ base: '14px', md: '16px' }}
                        color={'#1D1A16'}
                        maxW={'100%'}
                        letterSpacing={{ base: '0.28px', md: '0.32px' }}
                        lineHeight={{base: '21px', md: '24px'}}
                      />
                    </Box>
                  </Box>
                </AccordionPanel>
                <style global jsx>
                  {`
                    .faqAns a {
                      text-decoration: underline !important;
                    }
                    @media (hover: hover) {
                      .hoverStroke:hover path {
                        stroke: #fff;
                      }
                    }
                  `}
                </style>
              </>
            )}
          </AccordionItem>
        );
      });
    };

    return (
      <Accordion
        key={`section-${i}`}
        allowMultiple
        width={{ base: '100%' }}
        mb={'1rem'}
        borderRadius={"4px"}
        overflow={"hidden"}
      >
        <AccordionItem border="none">
          {({ isExpanded }) => (
            <>
              <AccordionButton
                bgColor={"#fff"}
                _hover={{
                  base: {
                    backgroundColor: '#fff',
                  },
                  lg: {
                    backgroundColor: '#F8F1E7',
                  }

                }}
                pl={'0px'}
                py={'0px'}
                onClick={() => mixPanelTrigger(faqObj.title, isExpanded)}
                minH={'80px'}
                pr={{ base: '20px', md: '24px' }}
              >
                <Image src={faqObj?.icon?.url} maxH={'80px'} />
                <chakra.div
                  mx={'8px'}
                  flex="1"
                  textAlign="left"
                  color={'#CF4520'}
                  textTransform={'uppercase'}
                  fontSize={{ base: '20px', md: '24px' }}
                  letterSpacing={{ base: '1.28px', md: '1.6px' }}
                  fontFamily="FedraSansStd-medium"
                  className="allSmallCase"
                  pb={'4px'}
                  lineHeight={{base: '110%', md: 'unset'}}
                >
                  {faqObj.title}
                </chakra.div>
                {isExpanded ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="25"
                      viewBox="0 0 24 25"
                      fill="none"
                    >
                      <path
                        d="M17 12.0762L7 12.0762"
                        stroke="#CF4520"
                        stroke-width="2"
                        stroke-linecap="square"
                      />
                    </svg>
                  ) : (
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 17L12 12M12 12L12 7M12 12L17 12M12 12L7 12"
                        stroke="#CF4520"
                        strokeWidth="2"
                        strokeLinecap="square"
                      />
                    </svg>
                  )}
              </AccordionButton>
              <AccordionPanel bgColor={'#fff'} pb={"0px"} px={'0px'} mx={'0px'}>
                {getChildren(faqObj.faqs)}
              </AccordionPanel>
            </>
          )}
        </AccordionItem>
      </Accordion>
    );
  });
};

  const renderSearchedFaqSection = faqItems => {
    let count = 1;

    const getChildren = () => {
      const children = faqItems?.map((faq, j) => {
        let c = ++count;
        return (
          <AccordionItem
            key={j}
            borderBottomWidth="1px"
            borderBottomColor="gray.400"
            borderTop="none"
            lineHeight="1.61"
            // pl={'0px'}
            mx={{ base: '-10px', md: '0px' }}
            _hover={{
                  base: {
                    background: '#fff',
                  },
                  lg: {
                    background: '#1D1A1608',
                  }

                }}
          >
            {({ isExpanded }) => (
              <>
                {' '}
                <AccordionButton
                  pt={4}
                  pb={4}
                  mb={isExpanded ? '-1.8rem' : 'unset'}
                  fontFamily="FedraSansStd-book"
                  borderTop="none"
                  _focus={{ shadow: 'transparent' }}
                  _hover={{
                    background: 'none !important',
                }}
                  alignItems={'flex-start'}
                >
                  <chakra.div
                    flex="1"
                    textAlign="left"
                    key={c}
                    color={'#1D1A16'}
                    fontSize={{ base: '14px', md: '16px' }}
                    fontFamily="FedraSansStd-medium"
                    letterSpacing={{ base: '0.28px', md: '0.32px' }}
                  >
                    {faq.question}
                  </chakra.div>

                  <svg
                    style={{ transform: isExpanded ? '' : 'rotate(180deg)' }}
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8 14L12 10L16 14"
                      stroke="#CF4520"
                      stroke-width="2"
                      stroke-linecap="square"
                    />
                  </svg>
                </AccordionButton>
                <AccordionPanel
                  pb={3}
                  fontFamily="FedraSansStd-book"
                  lineHeight="1.61"
                  pr={{ base: '2rem', md: '3rem' }}
                >
                  <Box display="block" width="100%" lineHeight="1.61">
                    <Box>
                      <StructuredTextParser
                        str={
                          typeof faq.answer === 'string'
                            ? faq.answer
                            : render(faq.answer)
                        }
                        region={region}
                        lang={lang}
                        fontSize={{ base: '14px', md: "16px" }}
                        color={'#1D1A16'}
                        letterSpacing={{ base: '0.28px', md: '0.32px' }}
                      />
                    </Box>
                  </Box>
                </AccordionPanel>
                <style global jsx>
                  {`
                    .faqAns a {
                      text-decoration: underline !important;
                    }
                  `}
                </style>
              </>
            )}
          </AccordionItem>
        );
      });
      ++count;
      return children;
    };

    return (
      <Accordion
        allowMultiple
        defaultIndex={[0]}
        width={{ base: '100%' }}
        mb={'1rem'}
      >
        {getChildren()}
      </Accordion>
    );
  };

  useEffect(() => {
    try {
      let searchResults = [];
      let relatedFaqsData = [];
      if (searchText && searchText?.trim()) {
        let txtToSearch = searchText.trim().toLowerCase();
        for (
          let index = 0;
          index < faqSection?.sectionContent.length;
          index++
        ) {
          let element = faqSection?.sectionContent?.[index];
          if (element?._modelApiKey === 'faq_section_v2') {
            let excludeFaqQst;
            for (let j = 0; j < element?.faqs?.length; j++) {
              const faqObj = element?.faqs[j];
              let faqAns = render(faqObj.answer)?.toLowerCase();
              if (
                faqObj?.question?.toLowerCase()?.includes(txtToSearch) ||
                faqAns?.includes(txtToSearch)
              ) {
                searchResults.push(faqObj);
                if (!excludeFaqQst) {
                  excludeFaqQst = faqObj.question;
                }
              }
            }
            if (excludeFaqQst && !relatedFaqsData?.length) {
              relatedFaqsData = relatedFaqsData.concat(
                element?.faqs.filter(f => f.question !== excludeFaqQst)
              );
            }
          }
        }
      }
      if (!!searchText?.length) {
        debouncedFn(searchText, searchResults);
      }
      setFilteredFaqs([...searchResults]);
      setRelatedFaqs([...relatedFaqsData]);
    } catch (error) {}
  }, [searchText]);

  useEffect(() => {
    const mainDiv = document.getElementById('#containerWrapper');
    const footerDiv = document.getElementById('#isoNewFooter')

    if (!mainDiv || !footerDiv) return;

    let registerPopup = document.getElementById('register-popup');
    const faqPopup = document.getElementById('faq-popup');

    // Handle register popup visibility
    if (isFaqOpen) {
      if (registerPopup) {
        registerPopup.style.display = 'none';
      }
    }
     else if (registerPopup) {
      registerPopup.style.display = 'unset';
    }

    if(!isFaqOpen && !faqPopup) return;

    // Remove previous keyframe style if exists
    const prevStyle = document.getElementById('dynamic-width-animation');
    if (prevStyle) prevStyle.remove();

    let mainDivOffsetW = mainDiv.offsetWidth + 'px'

    // Determine target width
    const currentWidth = registerPopup ? mainDivOffsetW : (isFaqOpen && isLargeScreen ? mainDivOffsetW :  'calc(100vw - 410px)');
    const targetWidth = registerPopup ? `calc(100vw - 410px)` : (isFaqOpen && isLargeScreen && !registerPopup ? `calc(100vw - 410px)` : mainDivOffsetW);

    // Create a keyframe dynamically for both slide right and left
    const style = document.createElement('style');
    style.id = 'dynamic-width-animation';
    style.innerHTML = `
      @keyframes widthAnim {
        from { width: ${currentWidth}; }
        to { width: ${targetWidth}; }
      }
    `;
    if ((isFaqMounted || isFaqOpen) && isLargeScreen) {
      document.head.appendChild(style);
      // Apply animation
      mainDiv.style.animation = 'widthAnim 0.5s ease forwards';
      footerDiv.style.animation = 'widthAnim 0.5s ease forwards';
    }

    // Handle scroll lock for small screens
    if (isFaqOpen) {
      if (!isLargeScreen) {
        document.body.style.position = 'relative';
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.position = '';
        document.body.style.overflow = 'auto';
      }
    } else {
      // Reset styles when popup is closed
      document.body.style.position = '';
      document.body.style.overflow = 'auto';
    }
  }, [isFaqOpen, isLargeScreen, isFaqMounted]);

  const slideIn = keyframes`
        from {
        transform: translateX(100%); /* start off-screen right */
        }
        to {
        transform: translateX(0); /* end at normal position */
        }
        `;

  const slideOut = keyframes`
        from {
        transform: translateX(0);
        }
        to {
        transform: translateX(100%);
        }`;
  const slideUp = keyframes`
    from {
    transform: translateY(100%); /* start off-screen right */
    }
    to {
    transform: translateY(0); /* end at normal position */
    }
    `;
  const slideDown = keyframes`
    from {
    transform: translateY(0); /* start off-screen right */
    }
    to {
    transform: translateY(100%); /* end at normal position */
    }
    `;
    const fadeIn = keyframes`
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    `;
    const fadeOut = keyframes`
      from {
        opacity: 1;
      }
      to {
        opacity: 0;
      }
    `;

  let ParentModal = useBreakpointValue({base: Modal, md: Box})
  let ParentModalContent = useBreakpointValue({base: ModalContent, md: Box})

  return (
    <Box
      id={faqSection?.sectionId}
      display="flex"
      flexDir={'column'}
      width="full"
      justifyContent={'center'}
      alignItems={'center'}
      bgColor={{ base: '#F8F1E7', md: '#EDE3D5' }}
      // bgGradient={
      //   'linear-gradient(180deg, rgba(237, 227, 213, 0) 54.58%, #EDE3D5 63%)'
      // }
      bgImage={{
        base: 'unset',
        md: faqSection?.styleConfig?.bgImage?.url,
      }}
      bgSize="100% auto"
      bgPosition="top"
      bgRepeat="no-repeat"
      pt={{ base: '80px', md: "120px", lg: '160px' }}
      pb={{ base: '72px', md: "120px", lg: '160px' }}
    >
      <Box
        pos={'relative'}
        height={'fit-content'}
        width={{ base: '100%', md: '750px', lg: '834px' }}
        mb={{ md: '2rem' }}
        px={"16px"}
      >
        <Box textAlign="left">
          <Text
            fontFamily={'FedraSansStd-book'}
             fontSize={{ base: '24px', md: '32px' }}
            // lineHeight={{ base: '24px', md: '52px' }}
            color={'#28231E'}
            fontWeight={400}
            textTransform={'uppercase'}
            letterSpacing={{ base: '3.2px', md: '7.12px' }}
            pb={"8px"}
            className="allSmallCase"
          >
            {faqSection?.sectionContent[0]?.text}
          </Text>
          <Text
            fontFamily="FedraSerifDisplayRegular"
            fontSize={{ base: '56px', md: '72px' }}
            mt={{base: '-5px', md: "unset"}}
            lineHeight={{ base: '56px', md: '50px' }}
            color={'#2F2F2F'}
            fontWeight={400}
            textTransform={'lowercase'}
            letterSpacing={{ base: '-1.12px', md: '-1.44px' }}
          >
            {faqSection?.sectionContent[1]?.text}
          </Text>
        </Box>
        <Text
          display={{ base: 'none', md: 'block' }}
          onClick={onFaqOpen}
          fontFamily={'FedraSansStd-book'}
          fontSize={{ md: '18px' }}
          fontWeight={400}
          color={'#1D1A16'}
          bgColor={'#fff'}
          w={{ md: '123px' }}
          h={{ md: '48px' }}
          lineHeight={{ md: '48px' }}
          borderRadius={{ md: '38px' }}
          textAlign={'center'}
          pl={'1.5rem'}
          pos={'absolute'}
          right={'0px'}
          cursor={'pointer'}
          bottom={'0rem'}
          _hover={{
            color: '#FFFFFF',
            bgColor: '#AB3312',
          }}
        className="hoverStroke"
        >
          <svg
            style={{ position: 'absolute', top: '0.9rem', left: '1rem' }}
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M16.9974 17L13.0474 13.05M14.9974 8C14.9974 11.866 11.8634 15 7.99744 15C4.13144 15 0.997437 11.866 0.997437 8C0.997437 4.13401 4.13144 1 7.99744 1C11.8634 1 14.9974 4.13401 14.9974 8Z"
              stroke="#CF4520"
              stroke-width="1.66667"
              stroke-linecap="round"
            />
          </svg>
          {faqSection?.sectionContent?.[8]?.text}
        </Text>
        <Box
          cursor={'pointer'}
          position={'relative'}
          pb={'1.5rem'}
          mt={'1.8rem'}
          onClick={onFaqOpen}
          display={{ base: 'block', md: 'none' }}
        >
          <Input
            onClick={onFaqOpen}
            isReadOnly
            pos={'relative'}
            type="text"
            border={'0.5px solid #CF4520'}
            placeholder={faqSection?.sectionContent?.[9]?.text}
            borderRadius={'4px'}
            _focus={{ border: '0.5px solid #CF4520' }}
            _placeholder={{
              color: '#1D1A16B2',
              fontSize: '16px',
              fontWeight: 400,
              fontFamily: 'FedraSansStd-book',
            }}
            _disabled={{}}
            paddingRight={'50px'}
            height={'59px'}
            onChange={e => setSearchText(e.target.value)}
            bgColor={'#FEFEFE66'}
            _hover={{ border: '0.5px solid #CF4520' }}
          />
          <svg
            style={{ position: 'absolute', right: '18px', top: '22px' }}
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M14.6668 14.2044L11.3752 10.9128M13.0002 6.70443C13.0002 9.92609 10.3885 12.5378 7.16683 12.5378C3.94517 12.5378 1.3335 9.92609 1.3335 6.70443C1.3335 3.48277 3.94517 0.871094 7.16683 0.871094C10.3885 0.871094 13.0002 3.48277 13.0002 6.70443Z"
              stroke="#1D1A16"
              stroke-width="1.66667"
              stroke-linecap="round"
            />
          </svg>
        </Box>
      </Box>

      <Box px="16px" width={{ base: '100%', md: '750px', lg: '834px' }}>
        {renderFaqSection(faqSection.sectionContent)}
      </Box>

      {isFaqMounted ? (
        <>
          <ParentModal isOpen={true}>
            <ParentModalContent>
              <Box
                display={isLargeScreen ? 'none' : { base: 'none', md: 'block' }}
                width={{ md: '100vw' }}
                bgColor={!isLargeScreen ? '#00000080' : '#1D1A16CC'}
                position={{ base: 'absolute', md: 'fixed' }} left={0} top={0} zIndex={1000}
                height={'100vh'}
                pointerEvents="auto"
                sx={{
                  touchAction: 'none',
                  overscrollBehavior: 'none',
                }}
                onTouchStart={(e) => e.preventDefault()}
                onTouchMove={(e) => e.preventDefault()}
                onClick={(e) => e.preventDefault()}
                animation={{ base: `${isFaqOpen ? fadeIn : fadeOut} 0.5s ease-out forwards`, md: `${isFaqOpen ? fadeIn : fadeOut} 0.5s ease-out forwards` }} />
              <Box
                overflowY={'auto'}
                animation={{
                  base: `${isFaqOpen ? slideUp : slideDown} 0.5s ease-out forwards`,
                  md: `${isFaqOpen ? slideIn : slideOut} 0.5s ease-out forwards`,
                }}
                id="faq-popup"
                position={'fixed'}
                height={'100%'}
                top={0}
                right={0}
                bgColor={'#fff'}
                width={{ base: '100%', md: '410px' }}
                zIndex={1001}
              >
                <Grid
                  height={'100%'}
                  gridTemplateRows={'max-content 1fr'}
                  overflow={'hidden'}
                >
                  <Box
                    px={'1rem'}
                    py={'24px'}
                    display={'flex'}
                    flexDir={'row'}
                    justifyContent={'space-between'}
                    width={'100%'}
                  >
                    <Box
                      onClick={() => {
                        if (!searchText && !filteredFaqs?.length) {
                          onFaqClose();
                          return;
                        }
                        setSearchText('');
                        setFilteredFaqs([]);
                      }}
                      cursor={'pointer'}
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 18 18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M7.50001 13.6885L2.8125 9.00098M2.8125 9.00098L7.50001 4.31347M2.8125 9.00098H15.1875"
                          stroke="black"
                          stroke-width="1.5"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </Box>
                    <Box
                      cursor={'pointer'}
                      onClick={() => {
                        onFaqClose();
                      }}
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 18 18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3.75 3.75L14.25 14.25M14.25 3.75L3.75 14.25"
                          stroke="black"
                          stroke-width="1.5"
                          stroke-linecap="round"
                        />
                      </svg>
                    </Box>
                  </Box>
                  <Box overflow={'auto'} pt={'16px'}>
                    <Text
                      fontFamily="FedraSerifDisplayRegular"
                      textAlign={'left'}
                      fontSize={{ base: '40px' }}
                      fontWeight={'300'}
                      lineHeight={{ base: '40px' }}
                      px={'1rem'}
                      letterSpacing={'-0.8px'}
                      mb={'1rem'}
                    >
                      {faqSection?.sectionContent?.[7]?.text}
                    </Text>

                    <Box px={'1rem'}>
                      <Box position={'relative'} pb={'1.5rem'}>
                        <Input
                          type="text"
                          border={'0.5px solid #CF4520'}
                          placeholder={faqSection?.sectionContent?.[9]?.text}
                          borderRadius={'4px'}
                          _focus={{ border: '0.5px solid #CF4520' }}
                          _placeholder={{
                            color: '#1D1A16B2',
                            fontSize: '16px',
                            fontWeight: 400,
                            fontFamily: 'FedraSansStd-book',
                          }}
                          paddingRight={'50px'}
                          height={'59px'}
                          lineHeight={'59px'}
                          value={searchText}
                          fontFamily="FedraSansStd-book"
                          onChange={e => setSearchText(e.target.value)}
                          css={{
                            transform: 'translate3d(0, 0, 0)',
                            backfaceVisibility: 'hidden',
                          }}
                          _hover={{ border: '0.5px solid #CF4520' }}
                        />
                        {searchText ?
                          <svg
                            onClick={() => setSearchText('')}
                            style={{ position: 'absolute', right: '18px', top: '20px', zIndex: 2, cursor: 'pointer' }}
                            width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M4.16602 4.16699L15.8327 15.8337M15.8327 4.16699L4.16602 15.8337" stroke="#1D1A16" stroke-width="1.66667" stroke-linecap="round" />
                          </svg> :
                          <svg
                            style={{ position: 'absolute', right: '18px', top: '22px', zIndex: 2, cursor: 'pointer' }}
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M14.6668 14.2044L11.3752 10.9128M13.0002 6.70443C13.0002 9.92609 10.3885 12.5378 7.16683 12.5378C3.94517 12.5378 1.3335 9.92609 1.3335 6.70443C1.3335 3.48277 3.94517 0.871094 7.16683 0.871094C10.3885 0.871094 13.0002 3.48277 13.0002 6.70443Z"
                              stroke="#1D1A16"
                              stroke-width="1.66667"
                              stroke-linecap="round"
                            />
                          </svg>}
                      </Box>
                      {!searchText && !filteredFaqs?.length
                        ? faqSection?.sectionContent?.map((i, j) => {
                          if (i?._modelApiKey === 'faq') {
                            return (
                              <Text
                                color={'#1D1A16'}
                                fontFamily="FedraSansStd-book"
                                fontSize={{ base: '14px', md: '16px' }}
                                lineHeight={{ md: '1.5' }}
                                bgColor={'#F8F1E7'}
                                mb={{ base: '1rem' }}
                                py={'10px'}
                                px={'16px'}
                                width={'fit-content'}
                                borderRadius={'100px'}
                                cursor={'pointer'}
                                onClick={() => {
                                  setFilteredFaqs([i]);
                                }}
                              >
                                {i.question}
                              </Text>
                            );
                          }
                        })
                        : null}
                      {searchText && !filteredFaqs?.length ? (
                        <Text
                          fontFamily={'FedraSansStd-book'}
                          fontSize={{ base: '16px' }}
                          textAlign={'center'}
                        >
                          {faqSection?.sectionContent?.[10]?.text}
                        </Text>
                      ) : null}

                      {filteredFaqs?.length
                        ? renderSearchedFaqSection(filteredFaqs)
                        : null}
                      {relatedFaqs?.length ? (
                        <>
                          <Box
                            cursor={'pointer'}
                            onClick={() => toggleShowRelatedFaqs(!showRelatedFaqs)}
                            px={{ base: '0.4rem', md: '1rem' }}
                            display={'flex'}
                            flexDir={'row'}
                            justifyContent={'space-between'}
                            alignItems={'center'}
                          >
                            <Text
                              fontFamily={'FedraSansStd-medium'}
                              fontSize={{ base: '24px' }}
                            >
                              {faqSection?.sectionContent?.[11]?.text}
                            </Text>
                            {showRelatedFaqs ? (
                              <svg
                                width="24"
                                height="25"
                                viewBox="0 0 24 25"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M7 12.0771H17"
                                  stroke="#CF4520"
                                  stroke-width="2"
                                  stroke-linecap="square"
                                />
                              </svg>
                            ) : (
                              <svg
                                width="24"
                                height="25"
                                viewBox="0 0 24 25"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M12 7.07715V12.0771M12 12.0771V17.0771M12 12.0771H7M12 12.0771H17"
                                  stroke="#CF4520"
                                  stroke-width="2"
                                  stroke-linecap="square"
                                />
                              </svg>
                            )}
                          </Box>
                          {showRelatedFaqs
                            ? renderSearchedFaqSection(relatedFaqs)
                            : null}
                        </>
                      ) : null}
                    </Box>
                  </Box>
                </Grid>
              </Box>
            </ParentModalContent>
          </ParentModal>
        </>
      ) : null}
         <style jsx global>{`
        .allSmallCase {
          font-variant: all-small-caps;
        }
      `}</style>
    </Box>
  );
}
